// src/ecrans/orchestrateur/EcranStockOrch.jsx — LE STOCK DE L'ORCHESTRATEUR
// (maquette v677, Orchestrateur › Stock, relevés 176 à 184 ; décisions 47, 53).
//
// Lu SEULEMENT par `ecran_orch_stock_lire`. Noms des logements venus du
// serveur : un code ne s'affiche jamais (panne du 30/10 : « l8 »).
// ÉCRITURE : le dépôt reste le geste existant (`stockApi.depotUpsert` ; son
// calcul est tenu par le lot A). AUCUNE ÉCRITURE NOUVELLE. Le seuil se lit ici :
// le serveur réserve `stock_set` à l'admin (écart noté, ouvrir ce droit = droits).

import { useMemo, useState } from "react";
import { TriangleAlert } from "lucide-react";
import SaisirDepotModal from "../../modals/SaisirDepotModal";
import { splitConsommables } from "../../lib/consommables-helpers";
import { useLecturePorte } from "../portes-g3";
import { StyleG3, EtatG3, LienAncienG3, Feuille, COULEURS as C } from "../nest-g3";
import { aRacheter, groupesCentral, nomDe, phraseSeuil, prixLu, quantiteLue, sousLeSeuil, texteListe } from "./stock-calculs";

const CSS = `
.gs-alerte{display:flex;gap:12px;align-items:center;flex-wrap:wrap;background:${C.aubP};border:1px solid ${C.aubBord};border-radius:14px;padding:12px 14px;margin:10px 0;color:${C.aubTx}}
.gs-alerte>div{flex:1;min-width:190px}
.gs-alerte>.ic{flex:0 0 30px;min-width:0;width:30px;height:30px;border-radius:9px;background:${C.aub};color:#fff;display:flex;align-items:center;justify-content:center}
.gs-alerte b{font-size:13px}
.gs-alerte ul{margin:6px 0 0;padding:0;list-style:none;display:flex;gap:6px;flex-wrap:wrap}
.gs-alerte li{font-size:12px;background:#fff;border:1px solid ${C.aubBord};border-radius:20px;padding:3px 10px;color:${C.encre}}
.gs-alerte li b{font-size:12px;color:${C.aubTx};margin-left:4px}
.gs-carte{background:#fff;border:1px solid ${C.bord};border-radius:14px;overflow:hidden;margin-bottom:12px}
.gs-carte .g3-ligne{border:0;border-bottom:1px solid #F0F2F1;border-radius:0;margin:0}
.gs-point{width:8px;height:8px;border-radius:50%;background:${C.vert};flex-shrink:0}
.gs-point.al{background:${C.aub}}
.gs-barre{display:block;height:3px;width:110px;max-width:100%;background:#EEF1EF;border-radius:2px;margin-top:5px}
.gs-barre i{display:block;height:3px;border-radius:2px;background:${C.vert}}
.gs-barre i.al{background:${C.aub}}
.gs-tete{display:flex;align-items:center;gap:10px;flex-wrap:wrap;margin:12px 0}
.gs-tete p{flex:1;margin:0;font-size:12.5px;color:${C.gris};min-width:150px}
.gs-groupe{display:flex;justify-content:space-between;font-size:12px;font-weight:600;background:#1C3A33;color:#fff;padding:10px 13px}
.gs-groupe small{font-weight:400;color:#C9D6D1}
.gs-q{font-size:12.5px;font-weight:600;text-align:right;flex-shrink:0}
.gs-q small{display:block;font-size:10.5px;font-weight:400;color:${C.gris}}
.gs-q.al{color:${C.aub}}
.gs-ligne{display:flex;justify-content:space-between;gap:10px;font-size:12.5px;padding:8px 0;border-bottom:1px solid ${C.bord}}
.gs-ligne small{display:block;color:${C.gris};font-size:11px}
`;

function pluriel(n, mot) {
  return `${n} ${mot}${n > 1 ? "s" : ""}`;
}

function FeuilleCourses({ central, onFermer }) {
  const sous = sousLeSeuil(central);
  const [copie, setCopie] = useState(null);
  const copier = async () => {
    try {
      await navigator.clipboard.writeText(texteListe(central));
      setCopie("Liste copiée.");
    } catch (e) {
      setCopie("La copie a échoué : recopiez la liste à la main.");
    }
  };
  return (
    <Feuille titre="La liste de courses" onFermer={onFermer} testId="stock-courses">
      <p className="g3-etat">Les produits sous leur seuil, avec ce qu'il faut racheter pour revenir à deux fois le seuil.</p>
      {sous.map((c) => (
        <div className="gs-ligne" key={c.cs_id}>
          <span>
            {nomDe(c)}
            <small>
              il en reste {c.qty} · seuil {c.seuil}
            </small>
          </span>
          <b>+ {aRacheter(c)}</b>
        </div>
      ))}
      <p style={{ marginTop: 12 }}>
        <button type="button" className="g3-bt p" onClick={copier} disabled={!sous.length}>
          Copier la liste
        </button>
      </p>
      {copie ? <p className="g3-etat" role="status">{copie}</p> : null}
    </Feuille>
  );
}

function FeuilleProduit({ produit: c, onFermer, onDeposer }) {
  return (
    <Feuille titre={nomDe(c)} onFermer={onFermer} testId="stock-produit">
      <p style={{ fontSize: 22, fontWeight: 600, margin: "0 0 2px" }}>
        {quantiteLue(c)} <span style={{ fontSize: 12, fontWeight: 400 }}>au central</span>
      </p>
      <p className="g3-etat" style={{ paddingTop: 0 }}>{phraseSeuil(c)}</p>
      <p className="g3-inter">Son seuil d'alerte</p>
      <div className="gs-ligne">
        <span>
          En dessous de cette quantité, il remonte dans la liste de courses
          <small>Réglé par l'admin pour l'instant.</small>
        </span>
        <b data-testid="stock-produit-seuil">{c.seuil ?? "—"}</b>
      </div>
      <p className="g3-inter">Ce qu'il coûte</p>
      <div className="gs-ligne">
        <span>Prix à l'unité</span>
        <b>{prixLu(c.prix_unite)}</b>
      </div>
      <div className="gs-ligne">
        <span>Catégorie</span>
        <b>{c.categorie === "kit" ? "Kit" : c.categorie === "operationnel" ? "Opérationnel" : "—"}</b>
      </div>
      <div className="gs-ligne">
        <span>Unité de prélèvement</span>
        <b>{c.unite || "—"}</b>
      </div>
      <p style={{ marginTop: 12 }}>
        <button type="button" className="g3-bt p" onClick={onDeposer}>
          Déposer ce produit dans un logement
        </button>
      </p>
    </Feuille>
  );
}

function FeuilleLinge({ data, onFermer }) {
  const [vue, setVue] = useState("linge");
  const [ouvert, setOuvert] = useState(null);
  return (
    <Feuille titre="Le linge et les consommables" onFermer={onFermer} testId="stock-linge">
      <div className="g3-seg" role="tablist">
        {[["linge", "Le linge"], ["conso", "Les consommables"]].map(([id, t]) => (
          <button key={id} type="button" role="tab" aria-selected={vue === id} className={vue === id ? "on" : ""} onClick={() => setVue(id)}>
            {t}
          </button>
        ))}
      </div>
      {vue === "linge" ? (
        <>
          <p className="g3-etat">Le même compte que l'admin : ce qui est dans chaque logement, et ce qui manque.</p>
          {data.logements.map((l) => (
            <button type="button" className="g3-ligne" key={l.id} data-testid="linge-logement" onClick={() => setOuvert(l.id === ouvert ? null : l.id)}>
              <span className="co">
                <span className="t">{l.nom}</span>
                <span className="s">{ouvert === l.id ? "L'admin n'a pas encore saisi la dotation." : "Compte pas encore saisi"}</span>
              </span>
              <span className="ch">›</span>
            </button>
          ))}
          {data.sans_nom ? <p className="g3-manque">{pluriel(data.sans_nom, "logement")} sans nom dans l'appli : à nommer par l'admin.</p> : null}
        </>
      ) : (
        <>
          <p className="g3-etat">Où acheter chaque produit, et son prix. Les prix sont ceux saisis par l'admin ; ils ne se modifient pas ici.</p>
          {data.produits.map((p) => (
            <div className="gs-ligne" key={p.cs_id}>
              <span>
                {nomDe(p)}
                <small>
                  {p.format || "format à renseigner"} · {p.enseigne || "enseigne à renseigner"}
                </small>
              </span>
              <b>{prixLu(p.prix)}</b>
            </div>
          ))}
        </>
      )}
    </Feuille>
  );
}

export default function EcranStockOrch({ cfg, stockCentral, stockApi, applyStockSnapshot, onAncien }) {
  const [lecture, relire] = useLecturePorte("ecran_orch_stock_lire");
  const [feuille, setFeuille] = useState(null); // linge | courses | { produit }
  const [depot, setDepot] = useState(null);
  const [erreurDepot, setErreurDepot] = useState(null);
  const [depotFait, setDepotFait] = useState(false);
  const data = lecture.etat === "ok" ? lecture.data : null;
  const sous = useMemo(() => sousLeSeuil(data?.central), [data]);
  const groupes = useMemo(() => groupesCentral(data?.central), [data]);
  const { kitProduits, operProduits } = useMemo(() => splitConsommables(cfg), [cfg]);

  const enregistrer = async (d) => {
    setErreurDepot(null);
    setDepotFait(false);
    try {
      applyStockSnapshot(await stockApi.depotUpsert(d));
      setDepot(null);
      setDepotFait(true);
      relire();
    } catch (e) {
      setErreurDepot(e?.message || "Le dépôt n'a pas été enregistré.");
    }
  };

  return (
    <div className="g3" data-testid="ecran-stock-orch">
      <StyleG3 />
      <style>{CSS}</style>
      <div className="g3-page">
        <EtatG3 lecture={lecture} quoi="Le stock" onRelire={relire} />
        {depotFait ? <p className="g3-etat" role="status" data-testid="stock-depot-fait">Dépôt enregistré.</p> : null}
        {data ? (
          <>
            {data.perimetre_vide ? (
              <p className="g3-manque">Vos logements n'ont pas pu être lus, ou aucun ne vous est confié.</p>
            ) : null}
            <button type="button" className="g3-ligne" data-testid="stock-bouton-linge" onClick={() => setFeuille("linge")}>
              <span className="co">
                <span className="t">Le linge et les consommables</span>
                <span className="s">ce qu'il y a dans chaque logement · où acheter chaque produit, et son prix</span>
              </span>
              <span className="ch">›</span>
            </button>
            {sous.length ? (
              <div className="gs-alerte" data-testid="stock-alerte">
                <div className="ic" title="Sous le seuil">
                  <TriangleAlert size={15} strokeWidth={2.2} aria-hidden="true" />
                </div>
                <div>
                  <b>{pluriel(sous.length, "produit")} sous le seuil</b>
                  <ul>
                    {sous.map((c) => (
                      <li key={c.cs_id}>
                        {nomDe(c)}
                        <b>
                          {c.qty} / {c.seuil}
                        </b>
                      </li>
                    ))}
                  </ul>
                </div>
                <button type="button" className="g3-bt" style={{ color: "#17211E" }} onClick={() => setFeuille("courses")}>
                  Voir la liste de courses ›
                </button>
              </div>
            ) : null}
            <div className="gs-tete">
              <p>{pluriel(data.central.length, "produit")} suivi{data.central.length > 1 ? "s" : ""} au central</p>
              <button type="button" className="g3-bt p" data-testid="stock-depot" disabled={!data.logements.length} onClick={() => setDepot({})}>
                Enregistrer un dépôt
              </button>
              <LienAncienG3 pour="confier, régler un manque" onClick={onAncien} testId="stock-ancien" />
            </div>
            <p className="g3-inter">Le central</p>
            {groupes.map((g) => (
              <section key={g.id} className="gs-carte" data-testid={`stock-groupe-${g.id}`}>
                <div className="gs-groupe">
                  {g.titre} <small>{pluriel(g.produits.length, "produit")}</small>
                </div>
                {g.produits.map((c) => (
                  <button type="button" className="g3-ligne" key={c.cs_id} data-testid="stock-produit-ligne" onClick={() => setFeuille({ produit: c })}>
                    <span className={`gs-point${sous.includes(c) ? " al" : ""}`} aria-hidden="true" />
                    <span className="co">
                      <span className="t">{nomDe(c)}</span>
                      {c.seuil && c.qty !== null ? (
                        <span className="gs-barre" aria-hidden="true">
                          <i className={sous.includes(c) ? "al" : ""} style={{ width: `${Math.min(100, (c.qty / (2 * c.seuil)) * 100)}%` }} />
                        </span>
                      ) : null}
                    </span>
                    <span className={`gs-q${sous.includes(c) ? " al" : ""}`}>
                      {quantiteLue(c)}
                      <small>seuil : {c.seuil ?? "—"}</small>
                    </span>
                    <span className="ch">›</span>
                  </button>
                ))}
              </section>
            ))}
            {data.central.length === 0 ? <p className="g3-etat">Le stock central est vide : les achats le remplissent.</p> : null}
          </>
        ) : null}
      </div>
      {feuille === "linge" && data ? <FeuilleLinge data={data} onFermer={() => setFeuille(null)} /> : null}
      {feuille === "courses" && data ? <FeuilleCourses central={data.central} onFermer={() => setFeuille(null)} /> : null}
      {feuille?.produit ? (
        <FeuilleProduit
          produit={feuille.produit}
          onFermer={() => setFeuille(null)}
          onDeposer={() => {
            setFeuille(null);
            setDepot({});
          }}
        />
      ) : null}
      {depot && data ? (
        <SaisirDepotModal
          allLogs={data.logements}
          kitProduits={kitProduits}
          operProduits={operProduits}
          stockCentral={stockCentral}
          defaultAuteur="menage"
          onSave={enregistrer}
          erreur={erreurDepot}
          onClose={() => {
            setDepot(null);
            setErreurDepot(null);
          }}
        />
      ) : null}
    </div>
  );
}
